import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { EyeOffIcon } from '@untitledui-icons/react/line'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { useNotes, type NoteListItem } from '@/hooks/api/useNotes'
import { useConversationsByIds } from '@/hooks/api/useConversations'
import { useAssistantsByIds } from '@/hooks/api/useAssistants'
import { NoteFilters, PriorityBadge } from './components'
import { statusConfig } from '@/types/notes'
import type { NotesListFilters } from '@/types/notes'
import { ConversationDrawer } from '@/pages/conversations/components'
import { useFilterStore } from '@/stores/filterStore'

const PERSIST_KEY = 'notes'

export function NotesPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isRestoringRef = useRef(false)

  // Use Zustand store for persistence
  const { getFilters, setFilters, clearFilters: clearStoredFilters } = useFilterStore()

  // Check if URL has filter params
  const hasUrlFilters = searchParams.has('filter')

  // Restore persisted filters to URL when navigating to page without filters
  useEffect(() => {
    if (isRestoringRef.current || hasUrlFilters) return

    const persisted = getFilters(PERSIST_KEY) as NotesListFilters | null
    if (persisted && Object.keys(persisted).length > 0) {
      isRestoringRef.current = true
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        params.set('filter', JSON.stringify(persisted))
        return params
      }, { replace: true })
      setTimeout(() => {
        isRestoringRef.current = false
      }, 0)
    }
  }, [hasUrlFilters, setSearchParams, getFilters])

  // Parse filters from URL
  const filters = useMemo((): NotesListFilters => {
    const filterParam = searchParams.get('filter')
    if (filterParam) {
      try {
        return JSON.parse(filterParam)
      } catch {
        return {}
      }
    }
    return {}
  }, [searchParams])

  // Persist filters to store when they change
  useEffect(() => {
    setFilters(PERSIST_KEY, filters as Record<string, unknown>)
  }, [filters, setFilters])

  // Clear handler for filter component
  const handleClearFilters = useCallback(() => {
    clearStoredFilters(PERSIST_KEY)
  }, [clearStoredFilters])

  // Pagination state
  const page = parseInt(searchParams.get('page') || '1', 10)
  const perPage = parseInt(searchParams.get('perPage') || '10', 10)

  // Sort state - default to updated_at DESC
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'updated_at',
    direction: 'descending',
  })

  // Selected conversation ID for drawer (from URL)
  const selectedConversationId = searchParams.get('selected')

  // Fetch notes
  const { data, isLoading, isFetching } = useNotes({
    filters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC',
    },
  })

  // Collect unique conversation IDs from notes for fetching assistant info
  const conversationIds = useMemo(() => {
    const ids = new Set<number>()
    data?.data?.forEach((note) => {
      if (note.conversation_id) {
        ids.add(note.conversation_id)
      }
    })
    return Array.from(ids)
  }, [data?.data])

  // Fetch conversations to get their assistant IDs
  const { data: conversations = [] } = useConversationsByIds(conversationIds, conversationIds.length > 0)

  // Create a map of conversation_id -> assistant_id
  const conversationAssistantMap = useMemo(() => {
    const map = new Map<number, number>()
    conversations.forEach((c) => {
      if (c.assistant_id) {
        map.set(c.id, c.assistant_id)
      }
    })
    return map
  }, [conversations])

  // Collect unique assistant IDs from conversations
  const assistantIds = useMemo(() => {
    const ids = new Set<number>()
    conversations.forEach((c) => {
      if (c.assistant_id) {
        ids.add(c.assistant_id)
      }
    })
    return Array.from(ids)
  }, [conversations])

  // Fetch assistants for their names
  const { data: assistants = [] } = useAssistantsByIds(assistantIds, assistantIds.length > 0)

  // Create a map of assistant id to name
  const assistantNameMap = useMemo(() => {
    const map = new Map<number, string>()
    assistants.forEach((a) => map.set(a.id, a.name))
    return map
  }, [assistants])

  const notes = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Handle filter change
  const handleFiltersChange = useCallback(
    (newFilters: NotesListFilters) => {
      const newParams = new URLSearchParams(searchParams)
      if (Object.keys(newFilters).length > 0) {
        newParams.set('filter', JSON.stringify(newFilters))
      } else {
        newParams.delete('filter')
      }
      newParams.set('page', '1') // Reset to first page
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('page', String(newPage))
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPerPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('perPage', String(newPerPage))
      newParams.set('page', '1')
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle row click - select the conversation and open the drawer
  const handleRowClick = useCallback(
    (note: NoteListItem) => {
      const newParams = new URLSearchParams(searchParams)
      // Use conversation ID for the drawer (not note ID)
      newParams.set('selected', String(note.conversation_id))
      newParams.set('tab', 'notes') // Open notes tab by default
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('selected')
    newParams.delete('tab')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  // Handle drawer tab change - update URL param instead of navigating away
  const handleDrawerTabChange = useCallback(
    (tabId: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('tab', tabId)
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Get current tab from URL
  const selectedTab = searchParams.get('tab') || 'notes'

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Helper to get assistant name for a note
  const getAssistantInfo = useCallback((note: NoteListItem) => {
    const assistantId = conversationAssistantMap.get(note.conversation_id)
    const assistantName = assistantId ? assistantNameMap.get(assistantId) : null
    return { assistantId, assistantName }
  }, [conversationAssistantMap, assistantNameMap])

  // Keyboard navigation (left/right arrows for note navigation)
  useEffect(() => {
    if (!selectedConversationId || !notes.length) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Find current note index by conversation ID
        const currentIndex = notes.findIndex(
          (n) => String(n.conversation_id) === selectedConversationId
        )
        if (currentIndex === -1) return

        const newIndex =
          e.key === 'ArrowLeft'
            ? Math.max(0, currentIndex - 1)
            : Math.min(notes.length - 1, currentIndex + 1)

        if (newIndex !== currentIndex) {
          const newNote = notes[newIndex]
          const newParams = new URLSearchParams(searchParams)
          newParams.set('selected', String(newNote.conversation_id))
          setSearchParams(newParams)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedConversationId, notes, searchParams, setSearchParams])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Check if filters are active
  const hasActiveFilters = Object.keys(filters).length > 0

  // Empty state
  if (notes.length === 0) {
    // If filters are active, show the header with filters so user can clear them
    if (hasActiveFilters) {
      return (
        <TableCard.Root size="sm" className="flex h-full flex-col">
          <TableCard.Header
            title={t('pages.notes.title')}
            description={t('pages.notes.description')}
            contentTrailing={
              <NoteFilters filters={filters} onFiltersChange={handleFiltersChange} onClear={handleClearFilters} />
            }
          />
          <div className="flex flex-1 items-center justify-center p-6">
            <EmptyState
              title={t('pages.notes.empty_title')}
              description={t('pages.notes.empty_filtered_description')}
            />
          </div>
        </TableCard.Root>
      )
    }

    // No filters, show simple empty state
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title={t('pages.notes.empty_title')}
          description={t('pages.notes.empty_description')}
        />
      </div>
    )
  }

  // Table view
  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('pages.notes.title')}
        description={t('pages.notes.description')}
        contentTrailing={
          <NoteFilters filters={filters} onFiltersChange={handleFiltersChange} onClear={handleClearFilters} />
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <Table
          aria-label="Notes"
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
        >
          <Table.Header>
            <Table.Head id="id" label={t('pages.notes.columns.id')} isRowHeader />
            <Table.Head id="updated_at" label={t('pages.notes.columns.last_updated')} allowsSorting />
            <Table.Head id="content" label={t('pages.notes.columns.content')} />
            <Table.Head id="assistant" label={t('pages.notes.columns.assistant')} />
            <Table.Head id="priority" label={t('pages.notes.columns.priority')} />
            <Table.Head id="is_private" label={t('pages.notes.columns.private')} className="text-center" />
            <Table.Head id="created_by" label={t('pages.notes.columns.created_by')} />
            <Table.Head id="assigned_to" label={t('pages.notes.columns.assigned_to')} />
            <Table.Head id="status" label={t('pages.notes.columns.status')} />
          </Table.Header>

          <AriaTableBody>
            {notes.map((note) => {
              const { assistantId, assistantName } = getAssistantInfo(note)
              const statusInfo = statusConfig[note.status]

              return (
                <Table.Row
                  key={note.id}
                  id={note.id}
                  className="cursor-pointer"
                  onAction={() => handleRowClick(note)}
                >
                  {/* ID */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {note.id}
                    </span>
                  </Table.Cell>

                  {/* Last updated */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {note.updated_at ? format(new Date(note.updated_at), 'dd/MM/yyyy') : '-'}
                    </span>
                  </Table.Cell>

                  {/* Content */}
                  <Table.Cell>
                    <span className="line-clamp-1 max-w-[200px] text-sm text-gray-900 dark:text-gray-100">
                      {note.content || '-'}
                    </span>
                  </Table.Cell>

                  {/* Assistant */}
                  <Table.Cell>
                    {assistantName && assistantId ? (
                      <Link
                        to={`/assistants/${assistantId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                      >
                        {assistantName}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                    )}
                  </Table.Cell>

                  {/* Priority */}
                  <Table.Cell>
                    <PriorityBadge priority={note.priority} />
                  </Table.Cell>

                  {/* Private */}
                  <Table.Cell className="text-center">
                    {note.is_private ? (
                      <EyeOffIcon className="mx-auto h-4 w-4 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </Table.Cell>

                  {/* Created by */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {note.created_by_name || '-'}
                    </span>
                  </Table.Cell>

                  {/* Assigned to */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {note.assigned_to_name || note.assigned_team_name || '-'}
                    </span>
                  </Table.Cell>

                  {/* Status */}
                  <Table.Cell>
                    <Badge
                      variant={note.status === 'open' ? 'success' : 'gray'}
                      size="sm"
                    >
                      {statusInfo.label}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </AriaTableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}

      {/* Conversation Drawer - shows the conversation with notes tab open */}
      <ConversationDrawer
        conversationId={selectedConversationId}
        onClose={handleDrawerClose}
        onTabChange={handleDrawerTabChange}
        initialTab={selectedTab}
      />
    </TableCard.Root>
  )
}

export default NotesPage
